import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import CategoryCard from "@/components/shared/CategoryCard";

/**
 * Mobile-only auto-advancing 2x2 grid carousel for categories.
 * Splits items into pages of 4; snaps horizontally; auto-advances every 4s.
 */
export default function CategoryGridCarousel({ categories = [], interval = 4000 }) {
  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const pages = [];
  for (let i = 0; i < categories.length; i += 4) pages.push(categories.slice(i, i + 4));

  useEffect(() => {
    if (paused || pages.length <= 1) return;
    const id = setInterval(() => {
      setPage((p) => (p + 1) % pages.length);
    }, interval);
    return () => clearInterval(id);
  }, [paused, pages.length, interval]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: page * el.clientWidth, behavior: "smooth" });
  }, [page]);

  // Detect manual scroll to update page indicator
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const p = Math.round(el.scrollLeft / el.clientWidth);
    if (p !== page) setPage(p);
  };

  return (
    <div
      className="sm:hidden"
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
      >
        {pages.map((chunk, pi) => (
          <div
            key={pi}
            className="snap-start shrink-0 w-full grid grid-cols-2 gap-3 pr-1"
          >
            {chunk.map((c, i) => (
              <CategoryCard key={c.slug} category={c} index={pi * 4 + i} />
            ))}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i)}
              aria-label={`Go to page ${i + 1}`}
              className={`h-1 transition-all duration-500 ${
                page === i ? "w-8 bg-[#0B2545]" : "w-4 bg-[#0B2545]/25"
              }`}
              data-testid={`categories-page-${i}`}
            />
          ))}
        </div>
        <Link
          to="/categories"
          className="text-[10px] md:text-xs font-mono uppercase tracking-widest text-[#0B2545] underline underline-offset-4"
        >
          View all <ArrowRight className="inline h-2.5 w-2.5 md:h-3 md:w-3 -mt-0.5" strokeWidth={2} />
        </Link>
      </div>
    </div>
  );
}
